import { Button } from "@freightsimple/ui-core/Controls/Button";
import { EmptyCell } from "@freightsimple/ui-core/Cells/EmptyCell";
import { LinkCell } from "@freightsimple/ui-core/Cells/LinkCell";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import {
  Table,
  type TableServerQuery,
} from "@freightsimple/ui-core/Table/Table";
import { TextCell } from "@freightsimple/ui-core/Cells/TextCell";
import type { Column } from "@freightsimple/ui-core/Table/Table";
import { WebhookDeliverySortKey } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDeliverySortKey";
import { WebhookDeliveryState } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDeliveryState";
import type { WebhookDelivery } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookDelivery";
import type { WebhookSummary } from "@freightsimple/generated-dashboard-openapi-client/models/WebhookSummary";
import type { UUID } from "@/Shared/types";
import {
  describeWebhookDeliveryTrigger,
  describeWebhookEventType,
} from "../Helpers/webhooksLabels";
import {
  webhooksOutcomeBadge,
  webhooksResponseCell,
  webhooksWhenCell,
} from "../Helpers/webhooksDeliveryCells";
import { webhooksRetryEligibility } from "../Helpers/webhooksRetryEligibility";

// The sortable columns are keyed by the API's sort keys, so the table's query maps straight
// onto the request. They are `serverSortable`, not comparators: the server orders the whole set,
// and this table only ever holds one page of it.
const columns: Column<WebhookDelivery>[] = [
  {
    key: WebhookDeliverySortKey.State,
    header: "Outcome",
    render: webhooksOutcomeBadge,
    serverSortable: true,
  },
  {
    key: WebhookDeliverySortKey.EventType,
    header: "Event",
    render: (d) => (
      <TextCell value={describeWebhookEventType(d.eventType)} weight="medium" />
    ),
    serverSortable: true,
  },
  {
    key: "shipment",
    header: "Shipment",
    render: (d) =>
      d.shipmentId ? (
        <LinkCell
          text="View"
          href={`/view-shipment?shipmentId=${d.shipmentId}`}
          external={false}
        />
      ) : (
        <EmptyCell />
      ),
  },
  {
    key: "url",
    header: "Endpoint",
    render: (d) => <TextCell value={d.webhookUrl} mono size="sm" />,
  },
  {
    key: WebhookDeliverySortKey.HttpStatus,
    header: "Response",
    render: webhooksResponseCell,
    serverSortable: true,
  },
  {
    key: "type",
    header: "Type",
    render: (d) => (
      <TextCell value={describeWebhookDeliveryTrigger(d.trigger)} />
    ),
  },
  {
    key: "attempts",
    header: "HTTP attempts",
    align: "right",
    render: (d) => <TextCell value={d.attemptCount} color="muted" />,
  },
  {
    key: WebhookDeliverySortKey.CreatedAt,
    header: "Sent",
    render: webhooksWhenCell,
    serverSortable: true,
  },
];

export interface WebhooksDeliveriesTableProps {
  /** One page, in the server's order. */
  deliveries: WebhookDelivery[];
  totalCount: number;
  maxCustomerRetries: number;
  /** The company's webhooks as they are now, so a paused or deleted one disables Redeliver. */
  webhooks: WebhookSummary[];
  /** Changes whenever the filters do, so the table returns to page 1. */
  resetPageKey: string;
  onQueryChange: (query: TableServerQuery) => void;
  /** Which delivery is being re-sent right now, so its button shows a spinner. */
  retryingDeliveryId: UUID | undefined;
  onView: (deliveryId: UUID) => void;
  onRetry: (deliveryId: UUID) => void;
}

/**
 * One row per delivery, an original send or a redelivery alike, paged and sorted on the server.
 * The attempt history of an event lives in the Details dialog, not nested here. Redeliver is
 * disabled with the reason rather than hidden: a customer looking at a failed row should see
 * that re-sending is a thing, and why it's not available right now.
 */
export function WebhooksDeliveriesTable({
  deliveries,
  totalCount,
  maxCustomerRetries,
  webhooks,
  resetPageKey,
  onQueryChange,
  retryingDeliveryId,
  onView,
  onRetry,
}: WebhooksDeliveriesTableProps) {
  return (
    <Table
      tableId="webhook-deliveries"
      columns={columns}
      data={deliveries}
      pageSize={25}
      pageSizeOptions={[25, 50, 100]}
      resetPageKey={resetPageKey}
      server={{ totalRows: totalCount, onQueryChange }}
      defaultSortKey={WebhookDeliverySortKey.CreatedAt}
      defaultSortDir="desc"
      rowKey={(d) => d.companyWebhookDeliveryId}
      rowHighlight={(d) =>
        d.state === WebhookDeliveryState.Delivered ||
        d.state === WebhookDeliveryState.Pending ||
        d.state === WebhookDeliveryState.Superseded
          ? undefined
          : "red"
      }
      emptyMessage="No deliveries match these filters"
      rowActions={(d) => {
        const eligibility = webhooksRetryEligibility(
          d,
          maxCustomerRetries,
          webhooks,
        );
        const id = d.companyWebhookDeliveryId;
        return (
          <Row gap={3}>
            <Button
              size="link"
              onClick={() => onView(id)}
              data-testid={`webhook-delivery-view-${id}`}
            >
              Details
            </Button>
            {eligibility.allowed ? (
              <Button
                size="link"
                loading={retryingDeliveryId === id}
                confirm={{
                  title: "Re-send this delivery?",
                  message: `We'll send the original event to ${d.webhookUrl} again.`,
                }}
                onClick={() => onRetry(id)}
                data-testid={`webhook-delivery-retry-${id}`}
              >
                Redeliver
              </Button>
            ) : (
              <Button
                size="link"
                disabled
                disabledMessage={eligibility.reason ?? ""}
                data-testid={`webhook-delivery-retry-${id}`}
              >
                Redeliver
              </Button>
            )}
          </Row>
        );
      }}
    />
  );
}
